import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  cameraBornee,
  cameraQuiCadre,
  cameraTiree,
  pointDeLaCarte,
  type Camera,
  type Contour,
  type Taille,
} from './borneDeLaCarte';
import { annulerLesAppuisLongs } from '../partage/deuxGestes';
import { cameraPincee, type Bornes, type DeuxDoigts } from './pincement';

/**
 * Ce qu'on regarde de la carte (ticket 36 de finition-v1 ; depuis le ticket 06 de
 * retours-testeurs).
 *
 * La molette **sur le fond** zoome et dézoome toute la carte, autour du pointeur ; sur une
 * bulle, c'est la carte qui décide (`surUneBulle`) : elle ouvre ou referme l'étape sur
 * place, et ne zoome pas. On glisse pour se déplacer ; au doigt, on pince pour zoomer
 * (ticket 15 du chantier `telephone`). Ouvrir une étape centre la caméra sur
 * elle, sans changer le zoom. « Recentrer » ramène le titre au milieu, à 100 %.
 *
 * La carte ne se perd jamais (ticket 05 de retours-testeurs) : au-delà de la limite de
 * `borneDeLaCarte.ts`, le glisser résiste, et la carte revient seule au lâcher. Le zoom,
 * « Recentrer » et une carte qui rétrécit (une étape refermée, une fenêtre plus petite) la
 * ramènent aussi dans la limite. Depuis le ticket 11 de retours-testeurs, plus de bouton de zoom :
 * la molette sur le fond suffit, et le coin en bas à droite est à l'ampoule de l'Aide.
 *
 * Ce fichier ne calcule rien de la carte (c'est le serveur, `domain/carte/carte.ts`) : il mesure
 * la surface et suit les gestes. C'est la seule chose de la carte qui dépende de ce que
 * l'écran fait, pas de ce que la feuille dit. Rien n'y est stocké.
 */

export interface Point {
  x: number;
  y: number;
}

/**
 * Au départ, on ne descend pas sous cette échelle pour faire tenir la carte : sous elle, les
 * titres ne se lisent plus. On glisse alors pour voir le reste.
 */
const PLANCHER_DE_DEPART = 0.42;
/** On peut toujours dézoomer jusque-là, et plus loin si la carte ouverte ne tient pas. */
const PLANCHER = 0.35;
const ECHELLE_MAX = 1.6;

/** Les deux doigts posés, dans l'ordre où ils se sont posés ; `null` s'il n'y en a pas deux. */
function lesDeuxDoigts(doigts: Map<number, Point>): DeuxDoigts | null {
  const [a, b] = [...doigts.values()];
  return a && b ? [a, b] : null;
}

/**
 * La caméra de la carte.
 *
 * `taille` : l'encombrement de ce que la carte montre, marges comprises.
 * `contour` : le rectangle des bulles visibles, étapes ouvertes comprises, et des papiers
 * visibles, qui ne quitte jamais tout à fait la surface.
 * `surUneBulle` : la molette est-elle pour une bulle ? Si oui, la carte l'a traitée et la
 * caméra ne zoome pas.
 */
export function useCameraDeCarte(
  taille: Taille,
  contour: Contour,
  surUneBulle: (evenement: WheelEvent) => boolean,
  /**
   * Regrouper (chantier theme-analyse, ticket 03) : les colonnes à cadrer au départ, à la place du
   * titre au milieu ; `cle` dit le regroupement, et en changer recadre, comme une carte qu'on ouvre.
   * `null` : la carte d'avant.
   */
  cadrage: { cle: string; contour: Contour } | null = null
) {
  const surface = useRef<HTMLDivElement>(null);
  const [ecran, setEcran] = useState<Taille | null>(null);
  const [camera, setCamera] = useState<Camera | null>(null);
  /** Molette et glisser suivent la main ; recentrer, centrer et le retour glissent. */
  const [direct, setDirect] = useState(true);
  const touchee = useRef(false);
  const pourLaBulle = useRef(surUneBulle);
  pourLaBulle.current = surUneBulle;
  /** Pour les écouteurs posés une fois : la limite et la caméra du moment. */
  const limite = useRef({ contour, ecran });
  limite.current = { contour, ecran };
  const regard = useRef(camera);
  regard.current = camera;
  /** Dans la limite, s'il y a une surface. */
  const dansLaLimite = useCallback((c: Camera): Camera => {
    const { contour: bord, ecran: surfaceMesuree } = limite.current;
    return surfaceMesuree ? cameraBornee(c, bord, surfaceMesuree) : c;
  }, []);
  /** Revenir dans la limite, en glissant. */
  const revenir = useCallback(() => {
    setDirect(false);
    setCamera((c) => (c ? dansLaLimite(c) : c));
  }, [dansLaLimite]);

  // La surface : mesurée tout de suite, puis à chaque changement de taille.
  useLayoutEffect(() => {
    const element = surface.current;
    if (!element) return;
    const mesurer = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width > 0 && height > 0) setEcran({ largeur: width, hauteur: height });
    };
    mesurer();
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  /** L'échelle à laquelle tout ce que la carte montre tient à l'écran. */
  const tient = ecran ? Math.min(ecran.largeur / taille.largeur, ecran.hauteur / taille.hauteur) : 1;
  const depart = Math.min(1, Math.max(PLANCHER_DE_DEPART, tient));
  const plusPetite = Math.min(PLANCHER, tient * 0.9);

  /*
   * Le cadrage de départ : le titre au milieu, le premier tour en entier s'il tient.
   * Refait quand la surface change de taille, tant qu'on n'a pas encore bougé la carte.
   */
  /*
   * Regrouper, ou revenir à la carte d'avant : le cadrage de départ repart, la carte glisse jusqu'à
   * lui. Tant qu'on n'a pas bougé, il suit les colonnes qui se mesurent.
   */
  const cleDuCadrage = cadrage?.cle ?? null;
  const cadrageVu = useRef(cleDuCadrage);
  const { gauche: cg, droite: cd, haut: ch, bas: cb } = cadrage?.contour ?? { gauche: 0, droite: 0, haut: 0, bas: 0 };
  useLayoutEffect(() => {
    if (!ecran) return;
    const change = cadrageVu.current !== cleDuCadrage;
    cadrageVu.current = cleDuCadrage;
    if (change) touchee.current = false;
    if (touchee.current) return;
    setDirect(!change);
    setCamera(
      cleDuCadrage === null
        ? { x: ecran.largeur / 2, y: ecran.hauteur / 2, s: depart }
        : cameraQuiCadre({ gauche: cg, droite: cd, haut: ch, bas: cb }, ecran, { min: PLANCHER_DE_DEPART, max: 1 })
    );
  }, [ecran, depart, cleDuCadrage, cg, cd, ch, cb]);

  const borner = useCallback(
    (s: number) => Math.min(Math.max(s, plusPetite), ECHELLE_MAX),
    [plusPetite]
  );

  /** Zoomer d'un facteur, en gardant immobile le point de la surface (px, py) : la main le suit. */
  const zoomer = useCallback(
    (facteur: number, px: number, py: number) => {
      touchee.current = true;
      setDirect(true);
      setCamera((c) => {
        if (!c) return c;
        const s = borner(c.s * facteur);
        const sous = pointDeLaCarte(c, { x: px, y: py });
        return dansLaLimite({ s, x: px - sous.x * s, y: py - sous.y * s });
      });
    },
    [borner, dansLaLimite]
  );

  // La molette : un écouteur à la main, parce qu'il doit pouvoir empêcher la page de défiler.
  useEffect(() => {
    const element = surface.current;
    if (!element) return;
    const molette = (evenement: WheelEvent) => {
      evenement.preventDefault();
      if (pourLaBulle.current(evenement)) return;
      const cadre = element.getBoundingClientRect();
      // Une souris donne des crans de 100, un pavé tactile une pluie de petits pas.
      const pas = evenement.deltaMode === 1 ? evenement.deltaY * 16 : evenement.deltaY;
      const facteur = Math.min(1.25, Math.max(0.8, Math.exp(-pas * 0.0012)));
      zoomer(facteur, evenement.clientX - cadre.left, evenement.clientY - cadre.top);
    };
    element.addEventListener('wheel', molette, { passive: false });
    return () => element.removeEventListener('wheel', molette);
  }, [zoomer]);

  /*
   * Glisser pour se déplacer. Ce n'est pas un glisser-déposer (interdit n°1) : rien ne
   * change de place sur la carte, c'est le regard qui se déplace. Un glisser qui part d'une
   * bulle ne l'ouvre pas en arrivant : le clic qui suit est avalé.
   *
   * Pincer pour zoomer (ticket 15 du chantier `telephone`) : deux doigts posés zooment autour
   * de leur milieu, dans les bornes de la molette (`pincement.ts`). Chaque doigt est suivi par
   * son numéro de pointeur, deux au plus. Passer d'un doigt à deux (et retour) repart de la
   * caméra du moment : la carte ne saute pas. Un pincement n'ouvre rien : il annule l'appui
   * long, et finit comme un glisser, qui avale le clic.
   */
  const tir = useRef<{ x: number; y: number; depuis: Camera; bouge: boolean } | null>(null);
  const doigts = useRef(new Map<number, Point>());
  const pince = useRef<{ depart: DeuxDoigts; depuis: Camera } | null>(null);
  /** La dernière caméra voulue par un geste, sans attendre que React l'ait dessinée. */
  const vue = useRef<Camera | null>(null);
  const bornes = useRef<Bornes>({ min: plusPetite, max: ECHELLE_MAX });
  bornes.current = { min: plusPetite, max: ECHELLE_MAX };

  const onPointerDown = useCallback(
    (evenement: React.PointerEvent) => {
      if (evenement.button !== 0 || !camera) return;
      // Les commandes, et les papiers posés sur la carte et leur « + Post-it » (chantier
      // post-it-et-listes, ticket 02) : un clic pour écrire, pas pour déplacer le regard ; un
      // glisser parti d'un papier déplace le papier (ticket 03, `../papiers`), jamais le regard.
      if ((evenement.target as HTMLElement).closest('.carte-commandes, .papier, .papiers-commandes')) return;
      // Le premier pointeur d'un geste : rien d'un geste d'avant ne reste suivi.
      if (evenement.isPrimary) {
        doigts.current.clear();
        pince.current = null;
      }
      if (doigts.current.size >= 2) return;
      doigts.current.set(evenement.pointerId, { x: evenement.clientX, y: evenement.clientY });
      if (doigts.current.size === 1) {
        vue.current = camera;
        tir.current = { x: evenement.clientX, y: evenement.clientY, depuis: camera, bouge: false };
        return;
      }
      // Le second doigt : on pince, à partir de la caméra du moment.
      const depart = lesDeuxDoigts(doigts.current);
      if (!depart) return;
      pince.current = { depart, depuis: vue.current ?? camera };
      tir.current = null;
      annulerLesAppuisLongs();
      touchee.current = true;
      surface.current?.classList.add('tableau--tire');
      setDirect(true);
    },
    [camera]
  );

  useEffect(() => {
    const poser = (voulue: Camera) => {
      const { contour: bord, ecran: surfaceMesuree } = limite.current;
      const c = surfaceMesuree ? cameraTiree(voulue, bord, surfaceMesuree) : voulue;
      vue.current = c;
      setCamera(c);
    };
    const bouger = (evenement: PointerEvent) => {
      if (!doigts.current.has(evenement.pointerId)) return;
      // Bouton relâché hors de la fenêtre : aucun pointerup n'est arrivé, le glisser s'arrête.
      if (evenement.buttons === 0) {
        lever(evenement);
        return;
      }
      doigts.current.set(evenement.pointerId, { x: evenement.clientX, y: evenement.clientY });
      const p = pince.current;
      if (p) {
        const cadre = surface.current?.getBoundingClientRect();
        const ici = (d: Point): Point => (cadre ? { x: d.x - cadre.left, y: d.y - cadre.top } : d);
        const maintenant = lesDeuxDoigts(doigts.current);
        if (!maintenant) return;
        const [a, b] = maintenant;
        poser(cameraPincee(p.depuis, [ici(p.depart[0]), ici(p.depart[1])], [ici(a), ici(b)], bornes.current));
        return;
      }
      const t = tir.current;
      if (!t) return;
      const dx = evenement.clientX - t.x;
      const dy = evenement.clientY - t.y;
      if (!t.bouge && Math.hypot(dx, dy) < 5) return;
      t.bouge = true;
      touchee.current = true;
      surface.current?.classList.add('tableau--tire');
      setDirect(true);
      poser({ ...t.depuis, x: t.depuis.x + dx, y: t.depuis.y + dy });
    };
    const lever = (evenement: PointerEvent) => {
      if (!doigts.current.delete(evenement.pointerId)) return;
      const p = pince.current;
      if (p) {
        pince.current = null;
        // Un doigt reste : il glisse, à partir de là où le pincement a laissé la carte. Il
        // compte comme ayant bougé : au lâcher, le clic est avalé.
        const reste = [...doigts.current.values()][0] ?? { x: 0, y: 0 };
        tir.current = { ...reste, depuis: vue.current ?? p.depuis, bouge: true };
      }
      if (doigts.current.size === 0) lacher();
    };
    const lacher = () => {
      const t = tir.current;
      tir.current = null;
      surface.current?.classList.remove('tableau--tire');
      if (!t?.bouge) return;
      // Tirée au-delà de la limite, la carte revient seule, en douceur.
      revenir();
      const avaler = (clic: MouseEvent) => {
        clic.stopPropagation();
        clic.preventDefault();
      };
      window.addEventListener('click', avaler, { capture: true, once: true });
      // Si aucun clic ne suit (lâché hors de la carte), on n'en avale pas un plus tard.
      window.setTimeout(() => window.removeEventListener('click', avaler, { capture: true }), 0);
    };
    window.addEventListener('pointermove', bouger);
    window.addEventListener('pointerup', lever);
    window.addEventListener('pointercancel', lever);
    return () => {
      window.removeEventListener('pointermove', bouger);
      window.removeEventListener('pointerup', lever);
      window.removeEventListener('pointercancel', lever);
    };
  }, [revenir]);

  // La carte rétrécit (une étape refermée) ou la surface change de taille : si on regardait
  // au-delà de la limite, on revient. Pendant un glisser, c'est le lâcher qui s'en charge.
  const { gauche, droite, haut, bas } = contour;
  useEffect(() => {
    const c = regard.current;
    if (c && !tir.current?.bouge && !pince.current && dansLaLimite(c) !== c) revenir();
  }, [gauche, droite, haut, bas, ecran, dansLaLimite, revenir]);

  /** On ouvre une étape : la caméra se centre sur elle, au même zoom. */
  const centrerSur = useCallback(
    (point: Point) => {
      if (!ecran) return;
      touchee.current = true;
      setDirect(false);
      setCamera((c) =>
        c ? { s: c.s, x: ecran.largeur / 2 - point.x * c.s, y: ecran.hauteur / 2 - point.y * c.s } : c
      );
    },
    [ecran]
  );

  /** « Recentrer » : le titre au milieu, à 100 % (décision d'Igor du 2026-09-26). */
  const recentrer = useCallback(() => {
    if (!ecran) return;
    touchee.current = true;
    setDirect(false);
    setCamera({ x: ecran.largeur / 2, y: ecran.hauteur / 2, s: 1 });
  }, [ecran]);

  /**
   * Ce qui est sous un point de l'écran (en coordonnées de la fenêtre), sur la carte : un papier
   * qu'on glisse suit la main, au zoom du moment (chantier post-it-et-listes, ticket 03). `null`
   * tant que la surface n'est pas mesurée.
   */
  const versLaCarte = useCallback((clientX: number, clientY: number): Point | null => {
    const c = regard.current;
    const cadre = surface.current?.getBoundingClientRect();
    if (!c || !cadre) return null;
    return pointDeLaCarte(c, { x: clientX - cadre.left, y: clientY - cadre.top });
  }, []);

  return { surface, camera, direct, onPointerDown, centrerSur, recentrer, versLaCarte };
}
